import React, { ComponentProps, memo, useCallback, useEffect, useMemo } from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { TabBar, TabBarIndicatorProps, TabBarProps, TabView } from 'react-native-tab-view';

import { CustomTabBarIndicator } from '@/components/CustomTabBarIndicator';
import { LEFT_ALIGNED_TAB_BAR_CONFIG, TopTabBarLabel } from '@/components/TopTabBar';
import { ds } from '@/components/lib/design.system';
import { AppColors } from '@/components/lib/theme';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { isWeb } from '@/utils/constants-platform-specific';

type TabRoute = {
    key: string;
    title: string;
    testID?: string;
};

export type LeftSideTabBarProps = {
    tabs: Array<{ title: string; id: string; testID?: string }>;
    setSelectedTab?: (id: string) => void;
    renderTabBarItem?: TabBarProps<TabRoute>['renderTabBarItem'];
    renderIndicator?: TabBarProps<TabRoute>['renderIndicator'];
    containerStyle?: StyleProp<ViewStyle>;
    tabBarStyle?: ViewStyle;
    tabBarLabelStyle?: ViewStyle;
    tabBarLabelFocusedColor?: AppColors;
    tabStyle?: ViewStyle;
    contentContainerStyle?: ViewStyle;
    scrollEnabled?: boolean;
    initialLayout?: Partial<ComponentProps<typeof TabView>['initialLayout']>;
    indicatorStyle?: ViewStyle;
    gap?: number;
    initialIndex?: number;
    location: string;
    testID?: string;
};

export const TABS_HEIGHT = 43;

const config = {
    ...LEFT_ALIGNED_TAB_BAR_CONFIG,
    tabBarIndicatorStyle: {
        ...(LEFT_ALIGNED_TAB_BAR_CONFIG.tabBarIndicatorStyle as object),
    },
};

/**
 * A Tab Bar with titles aligned on the left side with no screens
 */
export const LeftSideTabBar = memo(
    ({
        tabs,
        setSelectedTab,
        renderTabBarItem,
        indicatorStyle,
        renderIndicator,
        containerStyle,
        tabBarStyle,
        tabBarLabelStyle,
        tabBarLabelFocusedColor,
        tabStyle,
        contentContainerStyle,
        scrollEnabled = true,
        initialLayout,
        gap = 24,
        initialIndex = 0,
        location,
        testID,
    }: LeftSideTabBarProps) => {
        const [index, setIndex] = React.useState(initialIndex);

        useEffect(() => {
            setIndex(initialIndex);
        }, [initialIndex]);

        const routes = useMemo(
            () =>
                tabs.map(tab => ({
                    key: tab.id,
                    title: tab.title,
                    testID: tab.testID,
                })),
            [tabs]
        );

        const renderLabel = useCallback(
            (it: { focused: boolean; route: TabRoute }) => {
                if (renderTabBarItem) {
                    return undefined;
                }
                return (
                    <TopTabBarLabel
                        focusedColor={tabBarLabelFocusedColor}
                        style={[config.tabBarLabelStyle, tabBarLabelStyle]}
                        focused={it.focused}
                        testID={it.route.testID}
                    >
                        {it.route.title || ''}
                    </TopTabBarLabel>
                );
            },
            [renderTabBarItem, tabBarLabelFocusedColor, tabBarLabelStyle]
        );

        const renderTabBarIndicator = useCallback(
            (props: TabBarIndicatorProps<TabRoute>) => {
                return renderIndicator ? (
                    renderIndicator(props)
                ) : (
                    <CustomTabBarIndicator
                        width={'auto'}
                        getTabWidth={props.getTabWidth}
                        navigationState={props.navigationState}
                        layout={props.layout}
                        gap={props.gap}
                        duration={120}
                        style={props.style}
                    />
                );
            },
            [renderIndicator]
        );

        const renderTabBar = useCallback(
            (props: TabBarProps<TabRoute>) => {
                return (
                    <TabBar
                        onTabPress={({ route }) => {
                            setIndex(routes.findIndex(item => item.key === route.key));
                            setSelectedTab?.(route.key);
                            BetrAnalytics.trackEvent(AnalyticsEvent.TAB, {
                                tab_title: route.title,
                                location: location,
                            });
                        }}
                        {...props}
                        scrollEnabled={scrollEnabled}
                        style={[config.tabBarStyle, tabBarStyle]}
                        contentContainerStyle={contentContainerStyle}
                        gap={gap}
                        indicatorStyle={[config.tabBarIndicatorStyle, indicatorStyle]}
                        renderIndicator={renderTabBarIndicator}
                        tabStyle={[config.tabBarItemStyle, tabStyle]}
                        renderTabBarItem={renderTabBarItem}
                    />
                );
            },
            [
                scrollEnabled,
                tabBarStyle,
                contentContainerStyle,
                gap,
                indicatorStyle,
                renderTabBarIndicator,
                tabStyle,
                renderTabBarItem,
                routes,
                setSelectedTab,
                location,
            ]
        );

        const commonOptions = useMemo(() => ({ label: renderLabel }), [renderLabel]);

        return (
            <MaxWidthWrapper style={[styles.container, containerStyle]} testID={testID}>
                <View style={styles.tabsContainer}>
                    <TabView
                        onIndexChange={NO_OP}
                        initialLayout={initialLayout}
                        navigationState={{ index, routes }}
                        renderScene={NO_VIEW}
                        renderTabBar={renderTabBar}
                        commonOptions={commonOptions}
                    />
                </View>
            </MaxWidthWrapper>
        );
    }
);

const NO_OP = () => {};
const NO_VIEW = () => <></>;

const styles = StyleSheet.create({
    container: {
        backgroundColor: isWeb ? undefined : ds.colorValue.background.neutral.base,
        flex: isWeb ? undefined : 1,
        height: TABS_HEIGHT,
    },
    tabsContainer: {
        flexDirection: 'row',
        position: 'relative',
    },
});
